import type { Order, WindowBucket } from "./types-order";
import { RxStatus } from "./types-order";
import { apiClient } from "~/services/api-client";
import type {
  ApiOrderResponse,
  OrdersApiResponse,
  CallFlowType,
  CreateOrdersCallResponse,
  FiltersContextual,
} from "./types-interfaces";
import { ApiError, isAxiosError } from "~/shared/utils/api-error";
import { formatDateFromISO, formatTimeRequiredFromISO } from "~/shared/utils/formatting";

/**
 * Maps backend order_mapper response to frontend Order. Pass-through: all backend
 * fields preserved; only normalizes id to string and optional windows.
 */
export function transformApiOrder(apiOrder: ApiOrderResponse): Order {
  const pw = apiOrder.preferred_window;
  const ow = apiOrder.original_window;
  return {
    id: String(apiOrder.id),
    sm_number: apiOrder.sm_number,
    patient_name: apiOrder.patient_name ?? '',
    address: apiOrder.address ?? '',
    ship_to_city: apiOrder.ship_to_city ?? '',
    postal_code: apiOrder.postal_code ?? '',
    locationCode: apiOrder.locationCode,
    phone: apiOrder.phone ?? '',
    raw_phone: apiOrder.raw_phone,
    ai_inferred_phone: apiOrder.ai_inferred_phone,
    ship_to_phone_no: apiOrder.ship_to_phone_no,
    phone_secondary: apiOrder.phone_secondary,
    raw_phone_secondary: apiOrder.raw_phone_secondary,
    ai_inferred_phone_secondary: apiOrder.ai_inferred_phone_secondary,
    id_required: apiOrder.id_required,
    patient_id: apiOrder.patient_id ?? '',
    shipping_agent_code: apiOrder.shipping_agent_code ?? null,
    delivery_date: formatDateFromISO(apiOrder.delivery_date ?? ''),
    requested_delivery_date: formatDateFromISO(apiOrder.requested_delivery_date ?? ''),
    nav_status: apiOrder.nav_status ?? '',
    preferred_window: pw ? { start: pw.start, end: pw.end, label: pw.label, type: pw.type, startMinutes: pw.startMinutes, endMinutes: pw.endMinutes } : null,
    original_window: ow ? { start: ow.start, end: ow.end, label: ow.label, type: ow.type, startMinutes: ow.startMinutes, endMinutes: ow.endMinutes } : null,
    time_required: formatTimeRequiredFromISO(apiOrder.time_required ?? ''),
    white_glove_service: apiOrder.white_glove_service ?? false,
    rxStatus: apiOrder.rxStatus,
    clientId: apiOrder.clientId,
    updatedAt: apiOrder.updatedAt,
    lockStatus: apiOrder.lockStatus,
    isBlocked: apiOrder.lockStatus?.isBlocked ?? false,
    isBlockedBy: apiOrder.lockStatus?.blockedBy,
    lockExpiresAt: apiOrder.lockStatus?.expiresAt,
    to_be_dispatched: apiOrder.rxStatus === RxStatus.WAITING,
  };
}

export type FetchOrdersParams = {
  page?: number;
  limit?: number;
  search?: string;
  cities?: string[];
  dates?: string[];
  windows?: string[];
  locationCodes?: string[];
};

export type FetchOrdersPaginatedResult = {
  orders: Order[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  filtersContextual: FiltersContextual;
};

function buildQueryParams(params: FetchOrdersParams): Record<string, string | number> {
  const { page = 1, limit = 20, search, cities, dates, windows, locationCodes } = params;
  const qp: Record<string, string | number> = { page, limit };
  if (search) qp.search = search;
  const arrays: [string, string[] | undefined][] = [
    ['cities', cities], ['dates', dates], ['windows', windows], ['locationCodes', locationCodes],
  ];
  for (const [key, arr] of arrays) {
    if (arr && arr.length > 0) qp[key] = arr.join(',');
  }
  return qp;
}

function rethrowApiError(error: unknown): never {
  if (isAxiosError(error)) {
    const statusCode = error.response?.status;
    const responseData = error.response?.data as { message?: string } | undefined;
    const message = responseData?.message ?? error.message ?? 'Failed to fetch orders from API';
    throw new ApiError(message, statusCode, error.response?.data);
  }
  if (error instanceof ApiError) throw error;
  throw new ApiError(
    error instanceof Error ? error.message : 'An unexpected error occurred',
    undefined,
    error
  );
}

/**
 * Fetch orders with pagination and server-side filters/search.
 */
export async function fetchOrdersPaginated(
  params: FetchOrdersParams = {}
): Promise<FetchOrdersPaginatedResult> {
  try {
    const response = await apiClient.get<OrdersApiResponse>('/autocall/orders', {
      params: buildQueryParams(params),
    });

    if (!response.data) {
      throw new ApiError('Empty response from API', response.status);
    }

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? 'API request was not successful',
        response.status,
        response.data
      );
    }

    if (!response.data.data) {
      throw new ApiError('No order data in response', response.status, response.data);
    }

    const orders = response.data.data.map(transformApiOrder);
    const pagination = response.data.pagination ?? {
      total: orders.length,
      page: 1,
      limit: params.limit ?? 20,
      totalPages: 1,
    };
    const filtersContextual: FiltersContextual = response.data.filtersContextual ?? {
      cities: [],
      dates: [],
      windows: [],
      locationCodes: [],
    };
    return { orders, pagination, filtersContextual };
  } catch (error) {
    rethrowApiError(error);
  }
}

// ===========================
// Order Lock/Unlock Types
// ===========================

export type LockOrderResponse = {
  success: boolean;
  message: string;
  data: {
    orderId: string;
    lockStatus?: {
      isBlocked: boolean;
      blockedBy: string;
      blockedAt: string;
      expiresAt: string;
    };
  };
};

export type UnlockOrderResponse = {
  success: boolean;
  message: string;
  data: {
    orderId: string;
  };
};

/** Window sent to the backend: exact minutes from midnight. The backend derives hours and label. */
export type WindowMinutesPayload = {
  startMinutes: number;
  endMinutes: number;
};

export type UpdateAutocallOrderRequest = {
  updatedDeliveryDate?: string;
  updatedTimeRequired?: string;
  updatedWindow?: WindowMinutesPayload;
};

export type UpdateAutocallOrderResponse = {
  success: boolean;
  message: string;
  data: {
    orderId: string;
    updatedDeliveryDate?: string;
    updatedTimeRequired?: string;
    updatedWindow?: WindowBucket;
    updatedBy?: string;
  };
};

export type BulkWindowSkipReason =
  | "not_found"
  | "no_location_access"
  | "call_in_progress"
  | "locked_by_another_user"
  | "update_failed";

export type BulkUpdateOrderWindowResult = {
  window: WindowBucket;
  updated: string[];
  skipped: Array<{ orderId: string; reason: BulkWindowSkipReason }>;
};

type BulkUpdateOrderWindowResponse = {
  success: boolean;
  message: string;
  data?: BulkUpdateOrderWindowResult;
};

/**
 * Lock a single order (select)
 * Calls POST /autocall/orders/:orderId/lock
 */
export async function lockOrder(orderId: string): Promise<LockOrderResponse> {
  try {
    const response = await apiClient.post<LockOrderResponse>(
      `/autocall/orders/${encodeURIComponent(orderId)}/lock`
    );

    if (!response.data) {
      throw new ApiError('Empty response from API', response.status);
    }

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? 'Failed to lock order',
        response.status,
        response.data
      );
    }

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as { message?: string; data?: unknown } | undefined;
      const message = responseData?.message ?? error.message ?? 'Failed to lock order';
      throw new ApiError(message, statusCode, error.response?.data);
    }
    if (error instanceof ApiError) throw error;
    throw new ApiError(
      error instanceof Error ? error.message : 'An unexpected error occurred',
      undefined,
      error
    );
  }
}

/**
 * Unlock a single order (unselect)
 * Calls POST /autocall/orders/:orderId/unlock
 */
export async function unlockOrder(orderId: string): Promise<UnlockOrderResponse> {
  try {
    const response = await apiClient.post<UnlockOrderResponse>(
      `/autocall/orders/${encodeURIComponent(orderId)}/unlock`
    );

    if (!response.data) {
      throw new ApiError('Empty response from API', response.status);
    }

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? 'Failed to unlock order',
        response.status,
        response.data
      );
    }

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as { message?: string } | undefined;
      const message = responseData?.message ?? error.message ?? 'Failed to unlock order';
      throw new ApiError(message, statusCode, error.response?.data);
    }
    if (error instanceof ApiError) throw error;
    throw new ApiError(
      error instanceof Error ? error.message : 'An unexpected error occurred',
      undefined,
      error
    );
  }
}

/**
 * Update editable order fields on Auto Calls screen.
 * Calls PATCH /autocall/orders/:orderId
 */
export async function updateAutocallOrder(
  orderId: string,
  data: UpdateAutocallOrderRequest
): Promise<UpdateAutocallOrderResponse["data"]> {
  try {
    const response = await apiClient.patch<UpdateAutocallOrderResponse>(
      `/autocall/orders/${encodeURIComponent(orderId)}`,
      data
    );

    if (!response.data) {
      throw new ApiError("Empty response from API", response.status);
    }

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? "Failed to update order",
        response.status,
        response.data
      );
    }

    return response.data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as { message?: string } | undefined;
      const message = responseData?.message ?? error.message ?? "Failed to update order";
      throw new ApiError(message, statusCode, error.response?.data);
    }
    if (error instanceof ApiError) throw error;
    throw new ApiError(
      error instanceof Error ? error.message : "An unexpected error occurred",
      undefined,
      error
    );
  }
}

/**
 * Apply one delivery window to several orders (only the window changes).
 * Calls POST /autocall/orders/bulk-update-window. Orders the backend cannot update come back in `skipped`;
 * when none could be updated the backend answers 422 and the per-order result is still returned here.
 */
export async function bulkUpdateOrderWindow(
  orderIds: string[],
  window: WindowMinutesPayload
): Promise<BulkUpdateOrderWindowResult> {
  try {
    const response = await apiClient.post<BulkUpdateOrderWindowResponse>("/autocall/orders/bulk-update-window", {
      orderIds,
      window,
    });

    if (!response.data?.data) {
      throw new ApiError(response.data?.message ?? "Empty response from API", response.status, response.data);
    }

    return response.data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as BulkUpdateOrderWindowResponse | undefined;
      if (statusCode === 422 && responseData?.data) return responseData.data;
      const message = responseData?.message ?? error.message ?? "Failed to update delivery window";
      throw new ApiError(message, statusCode, error.response?.data);
    }
    if (error instanceof ApiError) throw error;
    throw new ApiError(
      error instanceof Error ? error.message : "An unexpected error occurred",
      undefined,
      error
    );
  }
}

/** Options for starting a call via the start-call API */
export interface StartCallOptions {
  orderIds: string[];
  callFlowType: CallFlowType;
}

export async function createOrdersCall(options: StartCallOptions): Promise<CreateOrdersCallResponse> {
  const { orderIds, callFlowType } = options;

  try {
    const response = await apiClient.post<CreateOrdersCallResponse>('/autocall/orders/start-call', {
      orderIds,
      callFlowType,
    });

    if (!response.data) {
      throw new ApiError('Empty response from API', response.status);
    }

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? 'API request was not successful',
        response.status,
        response.data
      );
    }

    return response.data;
  } catch (error) {
    // Handle axios errors
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as { message?: string } | undefined;
      const message = responseData?.message ?? error.message ?? 'Failed to create orders call';

      throw new ApiError(
        message,
        statusCode,
        error.response?.data
      );
    }

    // Re-throw ApiError as-is
    if (error instanceof ApiError) {
      throw error;
    }

    // Handle other errors
    throw new ApiError(
      error instanceof Error ? error.message : 'An unexpected error occurred',
      undefined,
      error
    );
  }
}
